import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { schoolsAPI, kitsAPI } from '../../utils/api';
import { useCartStore } from '../../store';
import { LoadingSpinner } from '../../components/common';
import QtyStepper from '../../components/common/QtyStepper';

const SchoolPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Page-level data
  const [school, setSchool] = useState(null);
  const [allKits, setAllKits] = useState([]);     // lightweight kit list for this school
  const [pageLoading, setPageLoading] = useState(true);

  // Filter state
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedGender, setSelectedGender] = useState('');

  // Active kit (full, with items)
  const [activeKit, setActiveKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(false);
  const [kitNotFound, setKitNotFound] = useState(false);

  // Cart state
  const [quantities, setQuantities] = useState({});
  const [selectedColors, setSelectedColors] = useState({});
  const [added, setAdded] = useState(false);
  const [fullKitAdded, setFullKitAdded] = useState(false);
  const { addKit, addFullKit } = useCartStore();

  // ── Load school + all kits for this school ─────────────
  useEffect(() => {
    Promise.all([schoolsAPI.getById(id), kitsAPI.getAll()])
      .then(([sRes, kRes]) => {
        setSchool(sRes.data);
        const kits = (kRes.data || []).filter(k => String(k.school_id) === String(id));
        setAllKits(kits);
        const getGradeVal = (g) => {
          if (typeof g !== 'string') return 0;
          const match = g.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        const grades = [...new Set(kits.map(k => k.grade).filter(Boolean))]
          .sort((a, b) => getGradeVal(a) - getGradeVal(b));
        if (grades.length > 0) setSelectedGrade(grades[0]);
      })
      .catch(() => navigate('/'))
      .finally(() => setPageLoading(false));
  }, [id, navigate]);

  // ── Load full kit when grade or gender changes ──────────
  useEffect(() => {
    if (!selectedGrade || allKits.length === 0) { setActiveKit(null); return; }

    const gradeKits = allKits.filter(k => k.grade === selectedGrade);
    if (gradeKits.length === 0) { setActiveKit(null); return; }

    const genders = [...new Set(gradeKits.map(k => k.gender))];
    const multi = genders.length > 1;

    let match;
    if (multi) {
      match = selectedGender ? gradeKits.find(k => k.gender === selectedGender) : null;
    } else {
      match = gradeKits[0];
    }

    if (!match) { setActiveKit(null); setKitNotFound(false); return; }

    setKitLoading(true);
    setActiveKit(null);
    setKitNotFound(false);
    kitsAPI.getById(match.id)
      .then(r => {
        setActiveKit(r.data);
        const qtyMap = {};
        const colorMap = {};
        r.data.items?.forEach(item => { 
          qtyMap[item.product_id] = item.default_qty; 
          if (item.colors) {
            colorMap[item.product_id] = item.colors.split(',').map(c => c.trim()).filter(Boolean);
          }
        });
        setQuantities(qtyMap);
        setSelectedColors(colorMap);
        setAdded(false);
        setFullKitAdded(false);
        setKitNotFound(false);
      })
      .catch(() => { setActiveKit(null); setKitNotFound(true); })
      .finally(() => setKitLoading(false));
  }, [selectedGrade, selectedGender, allKits]);

  const handleQtyChange = (productId, qty) =>
    setQuantities(prev => ({ ...prev, [productId]: qty }));

  const handleAddToCart = () => {
    if (!activeKit) return;
    addKit(activeKit, quantities, selectedColors);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const handleBuyFullKit = () => {
    if (!activeKit) return;
    addFullKit(activeKit);
    setFullKitAdded(true);
    setTimeout(() => setFullKitAdded(false), 2500);
  };

  if (pageLoading) return <LoadingSpinner />;
  if (!school) return null;

  // Derived values
  const availableGrades = [...new Set(allKits.map(k => k.grade).filter(Boolean))]
    .sort((a, b) => parseInt(a) - parseInt(b));
  const kitsForGrade = allKits.filter(k => k.grade === selectedGrade);
  const gendersForGrade = [...new Set(kitsForGrade.map(k => k.gender))];
  const hasMultipleGenders = gendersForGrade.length > 1;
  const activeItems = activeKit?.items?.filter(item => (quantities[item.product_id] ?? 0) > 0) || [];
  const total = activeItems.reduce((sum, item) => sum + item.price * (quantities[item.product_id] || 0), 0);
  const hasMissingColors = activeItems.some(item => {
    const itemColors = item.colors ? item.colors.split(',').map(c => c.trim()).filter(Boolean) : [];
    const chosenColors = selectedColors[item.product_id] || [];
    return itemColors.length > 0 && chosenColors.length === 0;
  });

  return (
    <div style={{ background: '#f8f7ff', minHeight: '100vh' }}>

      {/* ══ School Header + Grade Filters ══════════════════ */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 55%, #4f46e5 100%)',
        padding: '1.5rem 1rem 2rem',
      }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>

          {/* Back button */}
          <button
            onClick={() => navigate('/kits')}
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
              borderRadius: 20, padding: '0.35rem 1rem', cursor: 'pointer',
              fontSize: '0.85rem', marginBottom: '1.25rem', backdropFilter: 'blur(4px)',
            }}
          >← Back to Schools</button>

          {/* School name + address */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.75rem' }}>
            <div style={{ fontSize: '2.5rem', lineHeight: 1 }}>🏫</div>
            <div>
              <h1 style={{ color: '#fff', margin: 0, fontSize: 'clamp(1.2rem, 3.5vw, 1.9rem)', fontWeight: 900, lineHeight: 1.2 }}>
                {school.name}
              </h1>
              <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.85rem', marginTop: '0.3rem' }}>
                📍 {[school.address, school.city, school.state, school.pincode].filter(Boolean).join(', ')}
              </div>
            </div>
          </div>

          {/* ── Grade pills ── */}
          {availableGrades.length > 0 && (
            <div>
              <div style={{
                color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.6rem',
              }}>Select Grade</div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {availableGrades.map(g => {
                  const active = selectedGrade === g;
                  return (
                    <button
                      key={g}
                      onClick={() => { setSelectedGrade(g); setSelectedGender(''); }}
                      style={{
                        padding: '0.5rem 1.3rem', borderRadius: 999,
                        border: active ? '2px solid #fff' : '2px solid rgba(255,255,255,0.28)',
                        background: active ? '#fff' : 'rgba(255,255,255,0.1)',
                        color: active ? '#4f46e5' : '#fff',
                        fontWeight: active ? 800 : 600,
                        cursor: 'pointer', fontSize: '0.9rem',
                        transition: 'all 0.15s',
                        boxShadow: active ? '0 2px 12px rgba(0,0,0,0.2)' : 'none',
                      }}
                    >Grade {g}</button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Gender tabs (only when grade has boy + girl kits) ── */}
          {hasMultipleGenders && (
            <div style={{ marginTop: '0.9rem', display: 'flex', gap: '0.45rem' }}>
              {gendersForGrade.map(g => {
                const active = selectedGender === g;
                const label = g === 'boy' ? '👦 Boy' : g === 'girl' ? '👧 Girl' : '👤 All';
                return (
                  <button
                    key={g}
                    onClick={() => setSelectedGender(g)}
                    style={{
                      padding: '0.35rem 1rem', borderRadius: 999,
                      border: active ? '2px solid #a5b4fc' : '2px solid rgba(255,255,255,0.2)',
                      background: active ? 'rgba(165,180,252,0.28)' : 'transparent',
                      color: active ? '#e0e7ff' : 'rgba(255,255,255,0.58)',
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer', fontSize: '0.84rem',
                      transition: 'all 0.15s', textTransform: 'capitalize',
                    }}
                  >{label}</button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ══ Kit Content Area ════════════════════════════════ */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem 1rem 4rem' }}>

        {/* No kits at all for this school */}
        {availableGrades.length === 0 && (
          <div style={{ textAlign: 'center', padding: '5rem 1rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎒</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#374151', marginBottom: '0.5rem' }}>
              No kits available yet
            </div>
            <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
              Check back soon — kits will be added for this school.
            </div>
          </div>
        )}

        {/* Waiting: no grade selected yet */}
        {availableGrades.length > 0 && !selectedGrade && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>☝️</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#374151' }}>
              Select a grade above to see the kit
            </div>
          </div>
        )}

        {/* Waiting: grade selected but gender not yet picked */}
        {selectedGrade && hasMultipleGenders && !selectedGender && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>☝️</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#374151' }}>
              Select Boy or Girl above to see the kit
            </div>
          </div>
        )}

        {/* Loading kit */}
        {kitLoading && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2rem', display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</div>
            <p style={{ color: '#6b7280', marginTop: '0.75rem' }}>Loading kit…</p>
          </div>
        )}

        {/* Kit not found for this grade */}
        {!kitLoading && kitNotFound && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>😕</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#374151', marginBottom: '0.4rem' }}>
              No kit for Grade {selectedGrade}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
              Try another grade or check back later.
            </div>
          </div>
        )}

        {/* ── Active kit display ── */}
        {!kitLoading && activeKit && (
          <>


            {/* Buy Full Kit banner */}
            {activeKit.full_kit_price && (
              <div style={{
                marginBottom: '1rem', borderRadius: 12, padding: '1rem 1.25rem',
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                border: '2px solid #2563eb', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem',
              }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1e3a8a' }}>
                    🏷️ Buy Complete Kit — Save more!
                  </div>
                  <div style={{ fontSize: '0.83rem', color: '#1d4ed8', marginTop: '0.2rem' }}>
                    Get all {activeKit.items?.length} items as a ready-to-go bundle.
                  </div>
                  {total > parseFloat(activeKit.full_kit_price) && (
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.15rem' }}>
                      Individual total: <span style={{ textDecoration: 'line-through' }}>${total.toFixed(2)}</span>
                      {' '}— save{' '}
                      <span style={{ color: '#059669', fontWeight: 700 }}>
                        ${(total - parseFloat(activeKit.full_kit_price)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Bundle price</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#2563eb', lineHeight: 1 }}>
                      ${parseFloat(activeKit.full_kit_price).toFixed(2)}
                    </div>
                  </div>
                  <button
                    className={`btn btn-lg ${fullKitAdded ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={handleBuyFullKit}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {fullKitAdded ? '✓ Added!' : '🛒 Buy Full Kit'}
                  </button>
                </div>
              </div>
            )}

      {/* Items table */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '1rem' }}>
                <h2 style={{ fontWeight: 700, margin: 0, fontSize: '1rem' }}>Kit Items</h2>
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>You can customize the Kit items below:</span>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Unit Price</th>
                      <th>Qty</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeKit.items?.map(item => {
                      const qty = quantities[item.product_id] ?? 0;
                      const subtotal = item.price * qty;
                      const removed = qty === 0;
                      const rawColors = item.colors || item.product_colors || '';
                      const itemColors = rawColors
                        ? rawColors.split(',').map(c => c.trim()).filter(Boolean)
                        : [];
                      const chosenColors = selectedColors[item.product_id] || [];
                      return (
                        <React.Fragment key={item.product_id}>
                          <tr style={{ opacity: removed ? 0.35 : 1, transition: 'opacity 0.2s' }}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                {(item.image_data || item.image_url) ? (
                                  <img
                                    src={item.image_data || item.image_url}
                                    alt={item.name}
                                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', flexShrink: 0 }}
                                  />
                                ) : (
                                  <div style={{ width: 48, height: 48, borderRadius: 6, background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>📦</div>
                                )}
                                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{item.name}</div>
                              </div>
                            </td>
                            <td style={{ fontWeight: 600 }}>${parseFloat(item.price).toFixed(2)}</td>
                            <td>
                              <QtyStepper
                                value={qty}
                                onChange={v => handleQtyChange(item.product_id, v)}
                                min={0}
                                max={item.stock_qty}
                              />
                            </td>
                            <td style={{ fontWeight: 700, color: removed ? '#9ca3af' : '#2563eb' }}>
                              {removed ? <span style={{ fontSize: '0.78rem' }}>Removed</span> : `$${subtotal.toFixed(2)}`}
                            </td>
                          </tr>
                          {itemColors.length > 0 && !removed && (
                            <tr>
                              <td colSpan={4} style={{ padding: '0.3rem 0.75rem 0.6rem 3.2rem', background: '#f8fafc', borderTop: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    Choose color:
                                  </span>
                                  {itemColors.map(c => (
                                    <button
                                      key={c}
                                      onClick={() => {
                                        const current = selectedColors[item.product_id] || [];
                                        const next = current.includes(c)
                                          ? current.filter(x => x !== c)
                                          : [...current, c];
                                        setSelectedColors({ ...selectedColors, [item.product_id]: next });
                                        if (next.length > 0) {
                                          handleQtyChange(item.product_id, next.length);
                                        }
                                      }}
                                      style={{
                                        borderRadius: 999, padding: '0.15rem 0.6rem',
                                        fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                                        border: chosenColors.includes(c) ? '2px solid #2563eb' : '2px solid #e5e7eb',
                                        background: chosenColors.includes(c) ? '#dbeafe' : '#fff',
                                        color: chosenColors.includes(c) ? '#1e40af' : '#374151',
                                        transition: 'all 0.15s',
                                      }}
                                    >{c}</button>
                                  ))}
                                  {chosenColors.length === 0 && (
                                    <span style={{ fontSize: '0.68rem', color: '#f59e0b', fontWeight: 600, marginLeft: '0.25rem' }}>
                                      ↑ Select color(s)
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary & CTA */}
            <div className="card" style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', flexWrap: 'wrap', gap: '1rem',
            }}>
              <div>
                <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                  {activeItems.length} of {activeKit.items?.length} items selected
                </div>
                <div style={{ fontSize: '1.6rem', fontWeight: 900 }}>
                  Total: ${total.toFixed(2)}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  className={`btn ${added ? 'btn-secondary' : 'btn-primary'} btn-lg`}
                  onClick={handleAddToCart}
                  disabled={activeItems.length === 0 || hasMissingColors}
                  title={hasMissingColors ? 'Please select colors for the required items' : ''}
                >
                  {added ? '✓ Added to Cart!' : '🛒 Add items to cart'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SchoolPage;
